import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import {
  TReportLockedTooltipTrigger,
  useReportLockedTooltipTrigger,
} from "./useReportLockedTooltipTrigger";

interface IReportLockedTooltipContextValue {
  openTooltipId: string | null;
  lockedTooltipTrigger: TReportLockedTooltipTrigger[];
  onLockedTooltipToggle: (tooltipId: string, show: boolean) => void;
}

const ReportLockedTooltipContext = createContext<IReportLockedTooltipContextValue | null>(null);

export function getReportLockedTooltipId(reportCode: string, source: "card" | "pinned"): string {
  return `report-locked-${source}-${reportCode}`;
}

export function ReportLockedTooltipProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);
  const lockedTooltipTrigger = useReportLockedTooltipTrigger();

  const onLockedTooltipToggle = useCallback((tooltipId: string, show: boolean) => {
    setOpenTooltipId(show ? tooltipId : null);
  }, []);

  const value = useMemo(
    () => ({
      openTooltipId,
      lockedTooltipTrigger,
      onLockedTooltipToggle,
    }),
    [openTooltipId, lockedTooltipTrigger, onLockedTooltipToggle],
  );

  return <ReportLockedTooltipContext.Provider value={value}>{children}</ReportLockedTooltipContext.Provider>;
}

export function useReportLockedTooltipControl(tooltipId: string) {
  const context = useContext(ReportLockedTooltipContext);

  const onToggle = useCallback(
    (show: boolean) => {
      context?.onLockedTooltipToggle(tooltipId, show);
    },
    [context, tooltipId],
  );

  if (!context) {
    return {
      show: false,
      lockedTooltipTrigger: ["click"] as TReportLockedTooltipTrigger[],
      onToggle: () => {},
    };
  }

  return {
    show: context.openTooltipId === tooltipId,
    lockedTooltipTrigger: context.lockedTooltipTrigger,
    onToggle,
  };
}
